// Top-bar "Novedades" 📣 bell. A megaphone icon in the header with an unread
// count; clicking it opens a dropdown listing system announcements & updates
// (maintenance notices, new features, etc.). Read-only for everyone — the
// platform owner posts them from Platform Admin → Anuncios.
//
// Unread is tracked client-side: localStorage remembers the newest createdAt
// the user has seen; anything newer counts as unread until they open the panel.

import { useEffect, useRef, useState } from 'react';
import { Megaphone } from 'lucide-react';
import { getAnnouncements } from '../api.js';
import { sonarAnuncio } from '../sonidos.js';
import { intervaloVisible } from '../relojVisible.js';
// Los enlaces del anuncio se pueden pulsar (queja 66).
import { TextoConEnlaces } from './TextoConEnlaces.jsx';

const SEEN_KEY = 'deskte_announce_seen_at_v1';

const TYPE_META = {
  update: { emoji: '🎉', color: '#00ff88', es: 'Novedad', en: "What's new" },
  announcement: { emoji: '📣', color: '#00d4ff', es: 'Anuncio', en: 'Announcement' },
  maintenance: { emoji: '🛠️', color: '#ffb700', es: 'Mantenimiento', en: 'Maintenance' },
  // Queja 64 (2026-09-03). Sin esta linea, un anuncio de entrenamiento caeria
  // en el `|| TYPE_META.announcement` de abajo y se veria como un anuncio
  // cualquiera: el tipo existiria en el servidor y no en la pantalla.
  training: { emoji: '🎓', color: '#a78bfa', es: 'Entrenamiento', en: 'Training' },
};
const metaOf = (t) => TYPE_META[t] || TYPE_META.announcement;

// Each announcement can carry an optional English version (titleEn/bodyEn).
// English users see it; if the founder didn't write one, fall back to the
// primary (Spanish) text so the bell is never blank. Default/built-in items
// are already localized per-language, so they have no *En fields and just use
// title/body as-is.
const localized = (a, es) => ({
  title: es ? (a.title || a.titleEn || '') : (a.titleEn || a.title || ''),
  body: es ? (a.body || a.bodyEn || '') : (a.bodyEn || a.body || ''),
});

const fmtWhen = (iso, es) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const day = 86400000;
  if (diff < day && d.getDate() === new Date().getDate()) return es ? 'Hoy' : 'Today';
  if (diff < 2 * day) return es ? 'Ayer' : 'Yesterday';
  return d.toLocaleDateString(es ? 'es' : 'en', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Built-in welcome / "what's new" shown until the founder publishes real
// announcements (mirrors the Help Center's defaults). Disappears the moment
// any announcement is saved in the backend. Fixed createdAt so the unread
// badge is stable across renders.
const defaultItems = (es) => [{
  id: '__welcome',
  type: 'update',
  createdAt: '2026-06-09T12:00:00.000Z',
  active: true,
  title: es ? '🎉 ¡Novedades en Deskte!' : "🎉 What's new in Deskte!",
  body: es
    ? 'Agregamos: Nómina y Formularios (Paystub + 1099), Prospectos y Referidos, cambio de estado del cliente desde la ficha, Centro de Ayuda, un calendario nuevo y esta misma campana de Novedades. ¡Explóralos!'
    : 'New: Payroll & Forms (Paystub + 1099), Prospects & Referrals, change client status from the profile, a Help Center, a new date picker, and this Announcements bell. Check them out!',
}];

export function AnnouncementsBell({ lang, seenAt: seenAtProp, onSeen }) {
  const es = lang === 'es';
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(() => defaultItems(lang === 'es'));
  // Read-state ("hasta dónde leíste") is server-backed when the parent passes
  // seenAt/onSeen (syncs across devices via userPrefs); otherwise it falls back
  // to localStorage so the bell still works standalone.
  const controlled = typeof onSeen === 'function';
  const [seenLocal, setSeenLocal] = useState(() => { try { return localStorage.getItem(SEEN_KEY) || ''; } catch { return ''; } });
  const seenAt = controlled ? (seenAtProp || '') : seenLocal;
  const setSeenAt = (ts) => { if (controlled) onSeen(ts); else { try { localStorage.setItem(SEEN_KEY, ts); } catch {} setSeenLocal(ts); } };
  const ref = useRef(null);

  const load = async () => {
    // Use the backend's announcements when there are any; otherwise keep the
    // built-in welcome so the bell is never empty.
    try { const data = await getAnnouncements(); setItems(Array.isArray(data) && data.length ? data : defaultItems(es)); }
    catch { /* keep whatever's shown */ }
  };
  // POR QUE ESTO SE REFRESCA SOLO, Y NO SOLO AL ABRIR LA CAMPANA.
  //
  // Lissette, 2026-09-03: *"los anuncios no tienen sonido, solo la campana"*.
  // Y era verdad, aunque el sonido estuviera hecho y enganchado: esta campana
  // solo pedia los anuncios al CARGAR la pagina y al ABRIR el desplegable. Con
  // el CRM abierto, un anuncio nuevo no llegaba nunca a la pantalla, asi que el
  // contador no subia nunca, asi que no sonaba nunca. El sonido no fallaba: no
  // habia nada que lo disparara.
  //
  // La campana de notificaciones si se refresca sola cada 30 s. Esta hace lo
  // mismo, mas espaciado —un anuncio no es urgente como un aviso de cliente— y
  // con `intervaloVisible`, que para el reloj mientras nadie mira la pestaña y
  // refresca en el acto al volver a ella.
  const REFRESCO_MS = 90 * 1000;
  useEffect(() => {
    load();
    const parar = intervaloVisible(load, REFRESCO_MS);
    return parar;
  }, []);

  // Refresh when opening, and close on outside click.
  useEffect(() => {
    if (!open) return;
    load();
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const newest = items.reduce((m, a) => (a.createdAt && a.createdAt > m ? a.createdAt : m), '');
  const unread = items.filter((a) => a.createdAt && (!seenAt || a.createdAt > seenAt)).length;

  // Suena cuando LLEGA un anuncio nuevo (Lissette 2026-09-02, queja 10), con un
  // sonido distinto al de la campana de notificaciones.
  //
  // Arranca en null y la primera pasada solo apunta el numero: si sonara con el
  // valor inicial, cada recarga de la pagina pitaria por anuncios que ya
  // estaban ahi. Solo al SUBIR — leerlos baja el contador y eso no suena.
  const anterioresSinLeer = useRef(null);
  useEffect(() => {
    if (anterioresSinLeer.current !== null && unread > anterioresSinLeer.current) sonarAnuncio();
    anterioresSinLeer.current = unread;
  }, [unread]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && newest && newest > seenAt) setSeenAt(newest);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={toggle} className="dkt-hpill" title={es ? 'Novedades y anuncios' : 'News & announcements'} style={{ position: 'relative' }}>
        <Megaphone size={14} style={{ color: unread > 0 ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)' }} />
        {unread > 0 && (
          <span style={{ position: 'absolute', top: -2, right: -2, minWidth: 14, height: 14, padding: '0 3px', borderRadius: 100, background: 'var(--c-red,#ef4444)', color: '#fff', fontSize: '.6rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 50, width: 340, maxWidth: 'calc(100vw - 32px)', background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,.5)', overflow: 'hidden' }}>
          <div style={{ padding: '.7rem .85rem', borderBottom: '1px solid var(--c-border,#1a3550)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Megaphone size={14} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
            <span style={{ fontWeight: 800, fontSize: '.85rem', color: 'var(--c-white,#eef6ff)' }}>{es ? 'Novedades y Anuncios' : 'News & Announcements'}</span>
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {items.length === 0 ? (
              <div style={{ padding: '1.6rem 1rem', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.8rem', lineHeight: 1.6 }}>
                {es ? 'No hay anuncios por ahora. Aquí verás novedades del sistema y avisos.' : 'No announcements yet. System news and notices will show up here.'}
              </div>
            ) : (
              items.map((a) => {
                const m = metaOf(a.type);
                const L = localized(a, es);
                const isUnread = a.createdAt && (!seenAt || a.createdAt > seenAt);
                return (
                  <div key={a.id} style={{ display: 'flex', gap: 10, padding: '.7rem .85rem', borderBottom: '1px solid var(--c-border,#1a3550)', background: isUnread ? 'rgba(0,212,255,.05)' : 'transparent', opacity: a.active === false ? 0.55 : 1 }}>
                    <span style={{ width: 28, height: 28, flexShrink: 0, borderRadius: 8, background: m.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.95rem' }}>{m.emoji}</span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '.57rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: m.color }}>{es ? m.es : m.en}</span>
                        {a.active === false && <span style={{ fontSize: '.55rem', fontWeight: 700, color: 'var(--c-text3,#45688a)' }}>({es ? 'oculto' : 'hidden'})</span>}
                        <span style={{ marginLeft: 'auto', fontSize: '.62rem', color: 'var(--c-text3,#45688a)' }}>{fmtWhen(a.createdAt, es)}</span>
                      </div>
                      <div style={{ fontSize: '.83rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', marginTop: 1 }}>{L.title}</div>
                      {L.body && <div style={{ fontSize: '.74rem', color: 'var(--c-text2,#7aa3c0)', marginTop: 2, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}><TextoConEnlaces texto={L.body} /></div>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
